import { endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';
import { useCallback } from 'react';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { RepeatingTask } from 'shared/types/repeating-task';
import { Timestamp } from 'shared/types/timestamp';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { isNumber } from 'shared/utils/is-number';
import {
  getScheduleEndDate,
  updateScheduleEntryForDate,
} from 'shared/utils/schedules';

export type Options = {
  value?: number;
  increment?: number;
  toggle?: boolean;
  keepDetailOpen?: boolean;
};

export const useUpdateHabitScheduleEntry = () => {
  const today = useToday();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const track = useTrackEvents();
  const openTaskDetail = useOpenTaskDetail();

  return useCallback(
    (
      habit: Habit | RepeatingTask,
      date: Timestamp = new Date(),
      type: ScheduleEntryState = ScheduleEntryState.Complete,
      { value, increment, toggle, keepDetailOpen }: Options = {},
    ) => {
      if (isAfter(date, endOfDay(today))) {
        return;
      }

      let shouldCloseDetail = false;

      // Get the current entry value before updating to check if threshold will be reached
      const sortedSchedules = habit.schedules.toSorted(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      );

      // Find the schedule that applies to the date
      const scheduleForDate = sortedSchedules.find((schedule) => {
        const prevSchedule =
          sortedSchedules[sortedSchedules.indexOf(schedule) - 1];
        const startPointSchedule =
          getScheduleEndDate(prevSchedule) ?? new Date(0);
        return (
          !isBefore(date, startPointSchedule) &&
          (!schedule.endDate || !isAfter(date, getScheduleEndDate(schedule)!))
        );
      });

      if (scheduleForDate && type === ScheduleEntryState.Complete) {
        const entry = scheduleForDate.entries.find((entry) =>
          isSameDay(entry.date, date),
        );
        const entryVal = entry?.value ?? 0;

        // Calculate the new value that will be set
        const incrementValue = increment ?? 1;
        const newValue = Math.max(
          0,
          value ??
            (isNumber(increment)
              ? entryVal + incrementValue
              : (!scheduleForDate.frequency &&
                    entryVal >= scheduleForDate.completionTarget.count) ||
                  (!!scheduleForDate.frequency &&
                    entryVal &&
                    (getScheduleFrequencyDayTarget(scheduleForDate) ||
                      entryVal >= scheduleForDate.frequency.count)) ||
                  (toggle && !!entryVal)
                ? 0
                : entryVal + 1),
        );

        // Check if completion threshold will be reached
        if (
          scheduleForDate.frequency
            ? entryVal < scheduleForDate.frequency.count &&
              newValue >=
                (getScheduleFrequencyDayTarget(scheduleForDate) ??
                  scheduleForDate.frequency.count)
            : entryVal < scheduleForDate.completionTarget.count &&
              newValue >= scheduleForDate.completionTarget.count
        ) {
          shouldCloseDetail = true;
          track(ActionEvents.TaskComplete, {
            type: taskTypeToTrackingTaskType[habit.type],
          });
        }
      }

      // Use the helper function to update schedules
      const newSchedules = updateScheduleEntryForDate(habit, date, {
        today,
        type,
        value,
        increment,
        toggle,
      });

      // Update the tasks and finish
      updateTasks([
        { id: habit.id, schedules: newSchedules },
        // Uncomplete the subtasks
        ...(habit.childIds?.map((id) => ({
          id,
          completedAt: null,
        })) ?? []),
      ]);

      !keepDetailOpen && shouldCloseDetail && openTaskDetail(undefined);
    },
    [openTaskDetail, today, track, updateTasks],
  );
};
